<script setup lang="ts">
import { onUnmounted, ref, watch } from "vue";
import { lerpMap, maxDotNumber, stepMap } from "@zyzgroup/core-common";
import {
  __color,
  injectStyle,
  usePointerDownMovePoint$
} from "@zyzgroup/core-web";
import Box from "../base/Box.vue";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    min?: number;
    max?: number;
    step?: number;
    defaultValue?: number;
    unit?: string;
    showValue?: boolean;
    bgColor?: string;
    barColor?: string;
    barHeight?: number;
  }>(),
  {
    min: 0,
    max: 100,
    step: 1,
    defaultValue: 0,
    unit: "",
    showValue: false,
    bgColor: "#2d2d2d",
    barColor: "#2FA1D6",
    barHeight: 20
  }
);

const emits = defineEmits<{
  (event: "change", value: number): void;
}>();

const getStep = stepMap(props.min, props.max, props.step);
const getPercent = lerpMap(props.min, props.max, 0, 100);

const value = ref(getStep(props.defaultValue));
const percent = ref(maxDotNumber(getPercent(value.value), 2));

let mouseSubscription: any;

function mount(box) {
  const lerp = lerpMap(0, box.rect.width, props.min, props.max);
  const change = (clientX: number) => {
    value.value = getStep(lerp(clientX));
    percent.value = maxDotNumber(getPercent(value.value), 2);
    emits("change", value.value);
  };
  mouseSubscription = usePointerDownMovePoint$(box.el, {
    start: (e, mpg) => {
      // document.activeElement.blur();
      // e.preventDefault();
      change(mpg.current.x);
    }
  }).subscribe((mpg) => {
    change(mpg.current.x);
  });
}

const r = ref();
watch(r, () => {
  if (!r.value) {
    return;
  }
  /// for ce
  injectStyle(
    `
  :root {
    --input-range-box-background-hover-color: ${__color
      .set(props.bgColor)
      .lighten(0.05)
      .toString()};
    --input-range-box-foreground-hover-color: ${__color
      .set(props.barColor)
      .lighten(0.05)
      .toString()};
  }
  :host {
    --input-range-box-background-hover-color: ${__color
      .set(props.bgColor)
      .lighten(0.05)
      .toString()};
    --input-range-box-foreground-hover-color: ${__color
      .set(props.barColor)
      .lighten(0.05)
      .toString()};
  }
  `,
    r.value
  );
});

onUnmounted(() => {
  mouseSubscription?.unsubscribe();
});

defineExpose({});
</script>

<template>
  <div
    class="input-range-box"
    ref="r"
  >
    <div
      class="box"
      :style="{ height: props.barHeight + 'px', cursor: 'ew-resize' }"
    >
      <Box
        display="full"
        @mount="mount"
      >
        <div
          class="background"
          :style="{
            backgroundColor: props.bgColor,
            width: '100%',
            height: '100%'
          }"
        ></div>
        <div
          class="foreground"
          :style="{
            backgroundColor: props.barColor,
            width: percent + '%',
            height: '100%'
          }"
        ></div>
      </Box>
    </div>
    <slot :value="value">
      <label
        class="label"
        v-if="showValue"
      >
        {{ value }}{{ props.unit }}
      </label>
    </slot>
  </div>
</template>

<style scoped>
.input-range-box {
  position: relative;
  box-sizing: border-box;
  font-size: 1em;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  overflow: hidden;
}
.label {
  min-width: 3em;
  text-align: right;
}
.box {
  flex: 1 1 auto;
  position: relative;
}
.foreground {
  position: absolute;
  left: 0;
  top: 0;
}
.box:hover .background {
  background-color: var(--input-range-box-background-hover-color) !important;
}
.box:hover .foreground {
  background-color: var(--input-range-box-foreground-hover-color) !important;
}
</style>
